/**
 * Pure logic for task completion: aggregate quantity deltas per location and validate.
 * Used by taskCompletion.ts; fully unit-testable without DB.
 */
import { invariant } from './invariant';

export interface TaskSourceDestRow {
    taskId: string;
    quantity: number;
    quantityChange: number;
    sourceLocationId: string;
    existingSource: { id: string; quantity?: number } | null;
    sourceCurrentQty: number;
    destLocationId: string;
    existingDest: { id: string; quantity?: number } | null;
    destCurrentQty: number;
    itemTypeId: string;
    fromEventId: string | null;
    toEventId: string | null;
    fromStorageLocationId: string | null;
    toStorageLocationId: string | null;
}

export interface LocationDelta {
    currentQty: number;
    totalChange: number;
}

export interface NewLocationEntry {
    totalChange: number;
    id: string;
    itemTypeId: string;
    fromEventId?: string | null;
    toEventId?: string | null;
    fromStorageLocationId?: string | null;
    toStorageLocationId?: string | null;
}

export interface AggregatedDeltas {
    sourceDeltas: Map<string, LocationDelta>;
    destDeltas: Map<string, LocationDelta>;
    newSourceKeys: Map<string, NewLocationEntry & { fromEventId: string | null; fromStorageLocationId: string | null }>;
    newDestKeys: Map<string, NewLocationEntry & { toEventId: string | null; toStorageLocationId: string | null }>;
}

function sourceAggregationKey(row: TaskSourceDestRow): string {
    return `${row.itemTypeId}:${row.fromEventId ?? ''}:${row.fromStorageLocationId ?? ''}`;
}

function destAggregationKey(row: TaskSourceDestRow): string {
    return `${row.itemTypeId}:${row.toEventId ?? ''}:${row.toStorageLocationId ?? ''}`;
}

/**
 * Aggregate per-task source/dest quantity changes into per-location deltas.
 * Existing locations are keyed by locationId; new locations by (itemTypeId, event, storage).
 */
export function aggregateTaskDeltas(rows: TaskSourceDestRow[]): AggregatedDeltas {
    const sourceDeltas = new Map<string, LocationDelta>();
    const destDeltas = new Map<string, LocationDelta>();
    const newSourceKeys = new Map<string, NewLocationEntry & { fromEventId: string | null; fromStorageLocationId: string | null }>();
    const newDestKeys = new Map<string, NewLocationEntry & { toEventId: string | null; toStorageLocationId: string | null }>();

    for (const r of rows) {
        if (r.existingSource) {
            const s = sourceDeltas.get(r.sourceLocationId);
            if (s) s.totalChange += r.quantityChange;
            else sourceDeltas.set(r.sourceLocationId, { currentQty: r.sourceCurrentQty, totalChange: r.quantityChange });
        } else {
            const key = sourceAggregationKey(r);
            const s = newSourceKeys.get(key);
            if (s) s.totalChange += r.quantityChange;
            else
                newSourceKeys.set(key, {
                    totalChange: r.quantityChange,
                    id: r.sourceLocationId,
                    fromEventId: r.fromEventId,
                    fromStorageLocationId: r.fromStorageLocationId,
                    itemTypeId: r.itemTypeId,
                });
        }
        if (r.existingDest) {
            const d = destDeltas.get(r.destLocationId);
            if (d) d.totalChange += r.quantityChange;
            else destDeltas.set(r.destLocationId, { currentQty: r.destCurrentQty, totalChange: r.quantityChange });
        } else {
            const key = destAggregationKey(r);
            const d = newDestKeys.get(key);
            if (d) d.totalChange += r.quantityChange;
            else
                newDestKeys.set(key, {
                    totalChange: r.quantityChange,
                    id: r.destLocationId,
                    toEventId: r.toEventId,
                    toStorageLocationId: r.toStorageLocationId,
                    itemTypeId: r.itemTypeId,
                });
        }
    }

    return { sourceDeltas, destDeltas, newSourceKeys, newDestKeys };
}

export type ValidationResult = { ok: true } | { ok: false; message: string };

/**
 * Validate that checking the given tasks is allowed: source exists and has sufficient quantity.
 * When checked is false (unchecking), no source existence or quantity check is required.
 */
export function validateBatchTaskCompletion(rows: TaskSourceDestRow[], checked: boolean): ValidationResult {
    if (!checked) return { ok: true };
    for (const r of rows) {
        invariant(
            r.quantityChange >= 0,
            `quantityChange must be non-negative when completing (positive = items moving out); got ${r.quantityChange} for task ${r.taskId}`
        );
        if (!r.existingSource) {
            return { ok: false, message: 'Source location does not exist' };
        }
        const newSourceQty = r.sourceCurrentQty - r.quantityChange;
        if (newSourceQty < 0) {
            return {
                ok: false,
                message: `Insufficient inventory at source. Available: ${r.sourceCurrentQty}, Required: ${r.quantity}`,
            };
        }
    }
    return { ok: true };
}

/**
 * New quantity at an existing source after applying totalChange (positive = items moving out).
 * Clamped to >= 0.
 */
export function computeSourceQuantityAfterChange(currentQty: number, totalChange: number): number {
    return Math.max(0, currentQty - totalChange);
}

/**
 * New quantity at a new source. Stored as -totalChange (totalChange positive when checking = items out).
 */
export function computeNewSourceQuantity(totalChange: number): number {
    return -totalChange;
}

/**
 * New quantity at an existing dest after applying totalChange (positive = items moving in).
 */
export function computeDestQuantityAfterChange(currentQty: number, totalChange: number): number {
    return currentQty + totalChange;
}

/**
 * New quantity at a new dest. Stored as totalChange.
 */
export function computeNewDestQuantity(totalChange: number): number {
    return totalChange;
}
